import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Lock as PasswordIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Gavel as GavelIcon,
  Security as SecurityIcon,
  Favorite as FavoriteIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import NebenanHilfeLogo from '../UI/NebenanHilfeLogo';

const RegisterForm = ({ onSwitchToLogin }) => {
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    postalCode: '',
    password: '',
    passwordConfirm: '',
    contactMethod: 'email',
    acceptTerms: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [legalModal, setLegalModal] = useState({ open: false, title: '', type: '' });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
    setError('');
  };

  const handleOpenAgb = (e) => {
    e.preventDefault();
    setLegalModal({
      open: true,
      title: 'Allgemeine Geschäftsbedingungen (AGB)',
      type: 'agb',
    });
  };

  const handleOpenPrivacy = (e) => {
    e.preventDefault();
    setLegalModal({
      open: true,
      title: 'Datenschutzerklärung (DSGVO)',
      type: 'privacy',
    });
  };

  const handleCloseModal = () => {
    setLegalModal({ open: false, title: '', type: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.firstName || !formData.lastName || !formData.postalCode || !formData.password) {
      setError('Bitte fülle alle Pflichtfelder aus');
      return;
    }

    if (formData.contactMethod === 'email') {
      if (!formData.email) {
        setError('Bitte E-Mail-Adresse eingeben');
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setError('Ungültiges E-Mail-Format');
        return;
      }
    }

    if (formData.contactMethod === 'phone' && !formData.phone) {
      setError('Bitte Telefonnummer eingeben');
      return;
    }

    if (formData.password.length < 6) {
      setError('Das Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }

    if (formData.password !== formData.passwordConfirm) {
      setError('Passwörter stimmen nicht überein');
      return;
    }

    if (!formData.acceptTerms) {
      setError('Bitte akzeptiere die AGB und Datenschutzerklärung');
      return;
    }

    setLoading(true);
    const result = await register(formData);

    if (!result.success) {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <>
      <Card sx={{ maxWidth: 560, mx: 'auto', mb: 4, borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ p: { xs: 3, md: 5 } }}>
          <Box textAlign="center" mb={3} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <NebenanHilfeLogo size="large" />
            <Typography variant="h5" component="h1" sx={{ fontWeight: 800, color: 'text.primary', mt: 2, mb: 0.5 }}>
              Konto erstellen
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
              Werde Teil der NebenanHilfe.de Gemeinschaft
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
              <TextField
                fullWidth
                label="Vorname"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                margin="normal"
                disabled={loading}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><PersonIcon color="action" /></InputAdornment>,
                }}
              />
              <TextField
                fullWidth
                label="Nachname"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                margin="normal"
                disabled={loading}
              />
            </Box>

            <FormControl component="fieldset" sx={{ mt: 3, mb: 2 }}>
              <FormLabel component="legend" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Bevorzugte Kontaktmethode
              </FormLabel>
              <RadioGroup
                row
                name="contactMethod"
                value={formData.contactMethod}
                onChange={handleChange}
              >
                <FormControlLabel
                  value="email"
                  control={<Radio color="primary" />}
                  label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><EmailIcon fontSize="small" color="action" /> E-Mail</Box>}
                />
                <FormControlLabel
                  value="phone"
                  control={<Radio color="primary" />}
                  label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><PhoneIcon fontSize="small" color="action" /> Telefon</Box>}
                />
              </RadioGroup>
            </FormControl>

            {formData.contactMethod === 'email' ? (
              <TextField
                fullWidth
                label="E-Mail Adresse"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                margin="normal"
                disabled={loading}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><EmailIcon color="action" /></InputAdornment>,
                }}
              />
            ) : (
              <TextField
                fullWidth
                label="Telefonnummer"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                margin="normal"
                placeholder="+49 123 456789"
                disabled={loading}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><PhoneIcon color="action" /></InputAdornment>,
                }}
              />
            )}

            <TextField
              fullWidth
              label="Postleitzahl"
              name="postalCode"
              value={formData.postalCode}
              onChange={handleChange}
              margin="normal"
              inputProps={{ maxLength: 5 }}
              disabled={loading}
              InputProps={{
                startAdornment: <InputAdornment position="start"><LocationIcon color="action" /></InputAdornment>,
              }}
            />

            <TextField
              fullWidth
              label="Passwort"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              margin="normal"
              helperText="Min. 6 Zeichen"
              disabled={loading}
              InputProps={{
                startAdornment: <InputAdornment position="start"><PasswordIcon color="action" /></InputAdornment>,
              }}
            />

            <TextField
              fullWidth
              label="Passwort bestätigen"
              name="passwordConfirm"
              type="password"
              value={formData.passwordConfirm}
              onChange={handleChange}
              margin="normal"
              disabled={loading}
              InputProps={{
                startAdornment: <InputAdornment position="start"><PasswordIcon color="action" /></InputAdornment>,
              }}
            />

            {/* Legal Notice & AGB Acceptance Box */}
            <Box
              sx={{
                mt: 3,
                p: 2,
                bgcolor: 'action.hover',
                borderRadius: 2,
                border: '1px solid rgba(0,0,0,0.08)',
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                Mit der Registrierung akzeptieren Sie unsere{' '}
                <Link
                  component="button"
                  type="button"
                  onClick={handleOpenAgb}
                  underline="always"
                  sx={{ fontWeight: 700, color: 'primary.main', cursor: 'pointer' }}
                >
                  AGB
                </Link>{' '}
                und{' '}
                <Link
                  component="button"
                  type="button"
                  onClick={handleOpenPrivacy}
                  underline="always"
                  sx={{ fontWeight: 700, color: 'primary.main', cursor: 'pointer' }}
                >
                  Datenschutzerklärung
                </Link>
                .
              </Typography>

              <FormGroup sx={{ mt: 1.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="acceptTerms"
                      checked={formData.acceptTerms}
                      onChange={handleChange}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" fontWeight={600}>
                      Ich habe die AGB & Datenschutzerklärung gelesen und stimme zu.
                    </Typography>
                  }
                />
              </FormGroup>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || !formData.acceptTerms}
              startIcon={!loading ? <PersonAddIcon /> : null}
              sx={{
                mt: 3,
                mb: 3,
                py: 1.5,
                fontSize: '1.05rem',
                fontWeight: 700,
                borderRadius: 2,
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Konto kostenlos erstellen'}
            </Button>

            <Box textAlign="center">
              <Typography variant="body2">
                Bereits registriert?{' '}
                <Link
                  component="button"
                  type="button"
                  onClick={onSwitchToLogin}
                  sx={{ fontWeight: 700 }}
                >
                  Jetzt anmelden
                </Link>
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Interactive Legal Modal Dialog */}
      <Dialog
        open={legalModal.open}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, fontWeight: 800 }}>
          {legalModal.type === 'agb' ? <GavelIcon color="primary" /> : <SecurityIcon color="primary" />}
          {legalModal.title}
        </DialogTitle>
        <Divider />
        <DialogContent dividers>
          {legalModal.type === 'agb' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="h6" fontWeight={700}>
                Allgemeine Geschäftsbedingungen (AGB) für NebenanHilfe.de
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Stand: August 2026
              </Typography>
              <Divider />
              <Typography variant="subtitle1" fontWeight={700}>
                1. Geltungsbereich & Vertragsgegenstand
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                Diese Allgemeinen Geschäftsbedingungen gelten für die Nutzung der Nachbarschaftsplattform <strong>NebenanHilfe.de</strong>. Die Plattform ermöglicht ehrenamtliche Nachbarschaftshilfe, gegenseitige Unterstützung, lokales Leihen & Verschenken sowie Nachbarschaftskontakte im Quartier auf Grundlage christlicher Nächstenliebe und gegenseitigen Vertrauens.
              </Typography>

              <Typography variant="subtitle1" fontWeight={700}>
                2. 100% Kostenfreiheit & Gemeinnützigkeit
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                - <strong>100% Kostenlos</strong>: Die Nutzung der gesamten Plattform NebenanHilfe.de ist für alle Bürgerinnen und Bürger vollständig kostenfrei.<br />
                - <strong>Keine Abos & Keine Gebühren</strong>: Es gibt keine Registrierungsgebühren, keine Mitgliedsbeiträge und keine Verträge.<br />
                - <strong>Nachbarschaftshilfe</strong>: Nachbarn unterstützen sich gegenseitig ehrenamtlich und unentgeltlich.
              </Typography>

              <Typography variant="subtitle1" fontWeight={700}>
                3. Nutzerpflichten & Verhaltenskodex
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                Alle Mitglieder verpflichten sich zu wahrheitsgemäßen Angaben, respektvollem Umgang und der Einhaltung geltender Gesetze. Beleidigungen, Diskriminierung, Spam oder kommerzieller Missbrauch sind strengstens untersagt.
              </Typography>

              <Typography variant="subtitle1" fontWeight={700}>
                4. Haftungsausschluss
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                NebenanHilfe.de stellt die technische Plattform für das Miteinander im Quartier bereit. Für Absprachen, Hilfsdienste oder Leihgeschäfte, die direkt zwischen Nachbarn geschlossen werden, übernimmt die Plattform keine Haftung.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="h6" fontWeight={700}>
                Datenschutzerklärung (DSGVO) für NebenanHilfe.de
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Stand: August 2026
              </Typography>
              <Divider />
              <Typography variant="subtitle1" fontWeight={700}>
                1. Verantwortlicher für die Datenverarbeitung
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                Verantwortlich im Sinne der DSGVO ist das Betreiberteam von <strong>NebenanHilfe.de</strong> (support@nebenanhilfe.de).
              </Typography>

              <Typography variant="subtitle1" fontWeight={700}>
                2. Erhobene Daten & Verwendungszweck
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                Wir verarbeiten personenbezogene Daten (Vorname, Nachname, E-Mail-Adresse, Telefonnummer, Postleitzahl) ausschließlich zur Bereitstellung der lokalen Nachbarschaftshilfe, Verifizierung von Quartierangeboten und sicheren Kommunikation im System.
              </Typography>

              <Typography variant="subtitle1" fontWeight={700}>
                3. Datenübermittlung & Sicherheit
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                Ihre Daten werden vertraulich behandelt und niemals ohne Ihre ausdrückliche Zustimmung an Dritte oder Werbepartner weitergegeben. Alle Verbindungen sind mit moderner SSL/TLS-Verschlüsselung geschützt.
              </Typography>

              <Typography variant="subtitle1" fontWeight={700}>
                4. Ihre Rechte laut DSGVO
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                Sie haben jederzeit das Recht auf kostenlose Auskunft über Ihre gespeicherten Daten sowie das Recht auf Berichtigung, Sperrung oder Löschung Ihres Benutzerkontos.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseModal} variant="contained" sx={{ px: 3, fontWeight: 700 }}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RegisterForm;
